import { compileEvents } from "@/inference/engine/persist/compile";
import { InferenceSetup } from "@/inference/setup";
import { SDKConfiguration } from "@/inference/setup/config";
import { preparePayload } from "@/inference/utils/payload";
import { HeliumEncryption } from "@/providers/encryption";
import { RetargetingPlugsProvider } from "@/providers/retargeting-plugs";
import { sortHandlesByQuantity } from "@/utils";
import { ScoringHandler } from "../scoring";

const observers: IntersectionObserver[] = [];
const viewObserversSet: Record<string, boolean> = {};
const interactionsObserversSet: Record<string, boolean> = {};

const getPageElements = () => ({
  sections: Array.from(document.querySelectorAll("section")),
  products: Array.from(document.querySelectorAll("a")).filter(
    (a) => a.href.includes("/c/") || a.href.includes("/products/"),
  ),
});

function cleanHandle(element: HTMLElement, isSection: boolean) {
  const handle = isSection ? element.id : element.href;

  if (!handle) return;

  if (isSection) return handle?.split("?")?.at(0);

  const productHandle = handle?.split("/")?.at(-1);

  return productHandle?.split("?")?.at(0);
}

async function productsForTracking() {
  const productScore = sortHandlesByQuantity(
    ScoringHandler?.Product?.getComputedProductScores(),
  );

  const topProductScore = productScore.slice(0, 10);

  const products = await Promise.all(
    topProductScore.map(async (item) => {
      try {
        const details = await window.helium.datastore.getProduct({
          id: item.id,
        });
        return { ...details.details, category: details?.tags?.category };
      } catch (error) {
        console.error(`Error fetching product ${item.id}:`, error);
        return null;
      }
    }),
  );
  const validProducts = products.filter(Boolean).map((el) => {
    return {
      title: el.title,
      id: el.id,
      price: el.priceRange?.minVariantPrice?.amount,
      image: el?.featuredImage?.url,
      category: el.category,
      vendor: el?.vendor,
    };
  });
  return validProducts;
}

async function processProducts() {
  const trackedProducts = await productsForTracking();
  RetargetingPlugsProvider.track("HELIUM_INFERRED_PRODUCTS", {
    products: trackedProducts,
  });
}

export const EventListener = {
  initialise() {
    EventListener.viewtimeIntersectionObservers();
    EventListener.interactionObservers();
    EventListener.addFlushEventsListener();
  },

  interactionObservers() {
    const { products, sections } = getPageElements();

    sections.concat(products).forEach((element) => {
      if (interactionsObserversSet[element.id]) return;

      element.addEventListener("click", () => {
        const isSection = element.tagName === "SECTION";
        const isProduct = element.tagName === "A";

        const sectionId = cleanHandle(element, isSection);

        if (!sectionId) return;

        interactionsObserversSet[element.id] = true;

        if (isSection) {
          window.helium.dataprocessor.handleEvent({
            event: "click_section",
            section: {
              id: sectionId,
            },
          });
        } else if (isProduct) {
          window.helium.dataprocessor.handleEvent({
            event: "click_product",
            product: {
              handle: sectionId,
            },
          });
        }
      });
    });
  },

  observer: undefined,
  viewtimeIntersectionObservers(
    { isNavigation } = {
      isNavigation: false,
    },
  ) {
    let observer = this.observer;
    function addIntersectionObservers() {
      const elements = getPageElements();
      const sections = elements.sections;

      const a = elements.products;

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const isSection = entry.target.tagName === "SECTION";
            const isProduct = entry.target.tagName === "A";

            const sectionId = cleanHandle(entry.target, isSection);

            if (!sectionId || !entry.intersectionRatio) return;

            if (entry.isIntersecting) {
              window.helium.dataprocessor.handleStartIntersecting(
                sectionId,
                entry.time,
              );
            } else if (isSection) {
              window.helium.dataprocessor.handleEvent({
                event: "view_section",
                section: {
                  id: sectionId,
                },
                view_time: entry.time,
              });
            } else if (isProduct) {
              window.helium.dataprocessor.handleEvent({
                event: "view_product",
                product: {
                  handle: sectionId,
                },
                view_time: entry.time,
              });
            }
          });
        },
        {
          threshold: window.location.host.includes("gonoise") ? 0.1 : 1,
        },
      );

      sections.concat(a).forEach((element) => {
        if (element.getAttribute("data-helium-listener")) return;
        //if(viewObserversSet[element.id]) return;
        observer.observe(element);

        observers.push(observer);

        viewObserversSet[element.id] = true;
        element.setAttribute("data-helium-listener", "true");
      });
    }

    if (isNavigation) {
      setTimeout(() => {
        addIntersectionObservers();
        this.observer = observer;
      }, 0);
    } else {
      addIntersectionObservers();
      this.observer = observer;
    }
  },

  sendEvents() {
    const { filteredPayload, flattenedPayload } = preparePayload(
      compileEvents(),
      true,
    );

    if (window.location.hostname.includes("localhost")) {
      console.log("SENDING EVENTS", filteredPayload, flattenedPayload);
      return;
    }

    if (true) {
      RetargetingPlugsProvider.flushEvents(
        Object.fromEntries(
          Object.entries(flattenedPayload).filter(
            ([_key, value]) =>
              value !== undefined && value !== null && value !== 0,
          ),
        ),
      );
    }

    fetch(SDKConfiguration.EVENTS_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-helium-encryption": "lz",
      },
      body: JSON.stringify({
        token: HeliumEncryption.encrypt(filteredPayload),
      }),
      keepalive: true,
    });
    processProducts();
  },

  addFlushEventsListener() {
    if (!window.helium.logs) {
      EventListener.sendEvents();
    }

    InferenceSetup.registerInterval(
      setInterval(EventListener.sendEvents, SDKConfiguration.FLUSH_INTERVAL),
    );

    // InferenceSetup.registerInterval(
    //   setInterval(() => {
    //     compileEvents();
    //   }, 500),
    // );

    // window.addEventListener("beforeunload", EventListener.sendEvents);

    document.addEventListener("visibilitychange", logData);
  },

  cleanup() {
    // EventListener.sendEvents();

    observers.forEach((observer) => {
      observer.disconnect();
    });

    // window.removeEventListener("beforeunload", EventListener.sendEvents);

    document.removeEventListener("visibilitychange", logData);

    InferenceSetup.getIntervals().forEach((id) => clearInterval(id));
    InferenceSetup.getTimeouts().forEach((id) => clearTimeout(id));
  },
};

function logData() {
  if (document.visibilityState === "hidden") {
    EventListener.sendEvents();
  }
}
